/**
 * Voice AI — Global Media Library Modal
 * Single instance rendered in app-shell.js (top level = no stacking context issues).
 * Subscribes to mediaLibraryService and shows a <voice-modal> with file grid.
 * All API calls go through mediaService (architecture rule: components are thin).
 */

import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../styles/design-system.js';
import { iconStyles } from '../styles/icon-styles.js';
import { mediaLibraryService } from '../services/media-library-service.js';
import { mediaService } from '../services/media-service.js';
import { confirmService } from '../services/confirm-service.js';
import './voice-modal.js';

const FILE_TYPE_ICONS = {
  audio: 'fa-waveform-lines',
  image: 'fa-image',
  video: 'fa-film',
  document: 'fa-file-lines',
};

const FILE_TYPE_COLORS = {
  audio: { bg: 'rgba(37, 99, 235, 0.1)', text: '#2563EB' },
  image: { bg: 'rgba(139, 92, 246, 0.1)', text: '#7C3AED' },
  video: { bg: 'rgba(202, 138, 4, 0.1)', text: '#CA8A04' },
  document: { bg: 'rgba(107, 114, 128, 0.1)', text: '#6B7280' },
};

const FILTER_PILLS = [
  { key: null, label: 'All' },
  { key: 'audio', label: 'Audio', icon: 'fa-waveform-lines' },
  { key: 'image', label: 'Images', icon: 'fa-image' },
  { key: 'video', label: 'Video', icon: 'fa-film' },
  { key: 'document', label: 'Documents', icon: 'fa-file-lines' },
];

export class VoiceMediaLibrary extends LitElement {
  static properties = {
    _open: { state: true },
    _fileType: { state: true },
    _tags: { state: true },
    _uploadedBy: { state: true },
    _multiple: { state: true },
    _selectedIds: { state: true },
    _accept: { state: true },
    _files: { state: true },
    _loading: { state: true },
    _uploading: { state: true },
    _uploadProgress: { state: true },
    _searchQuery: { state: true },
    _playingId: { state: true },
    _dragOver: { state: true },
  };

  static styles = [
    designSystem,
    componentStyles,
    css`
      .library-toolbar {
        padding: var(--space-4) var(--space-5);
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
        border-bottom: 1px solid var(--color-border-light, #E8E5E0);
      }

      .search-bar {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        background: var(--color-bg-tertiary);
        border: 1px solid var(--color-border-light, #E8E5E0);
        border-radius: var(--radius-md, 8px);
        padding: 0 var(--space-3);
        height: 36px;
      }

      .search-bar i {
        color: var(--color-text-tertiary);
        font-size: 13px;
        flex-shrink: 0;
      }

      .search-bar input {
        flex: 1;
        border: none;
        background: transparent;
        outline: none;
        font-size: var(--text-sm);
        color: var(--color-text-primary);
        font-family: var(--font-body);
      }

      .search-bar input::placeholder {
        color: var(--color-text-tertiary);
      }

      .filter-pills {
        display: flex;
        gap: var(--space-2);
        flex-wrap: wrap;
      }

      .pill {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 4px 12px;
        border-radius: 100px;
        font-size: var(--text-xs, 11px);
        font-weight: var(--font-medium, 500);
        border: 1px solid var(--color-border-light, #E8E5E0);
        background: transparent;
        color: var(--color-text-secondary);
        cursor: pointer;
        transition: all 0.15s ease;
        font-family: var(--font-body);
      }

      .pill:hover {
        border-color: var(--color-primary);
        color: var(--color-primary);
      }

      .pill.active {
        background: var(--color-primary);
        border-color: var(--color-primary);
        color: white;
      }

      .pill i { font-size: 11px; }

      /* ─── Upload Drop Zone ─── */
      .upload-dropzone {
        margin: var(--space-4) var(--space-5) 0;
        padding: var(--space-4);
        border: 2px dashed var(--color-border-light, #E8E5E0);
        border-radius: var(--radius-lg, 12px);
        text-align: center;
        cursor: pointer;
        transition: all 0.15s ease;
        background: transparent;
      }

      .upload-dropzone:hover,
      .upload-dropzone.drag-over {
        border-color: var(--color-primary);
        background: var(--color-primary-light);
      }

      .upload-dropzone i {
        font-size: 1.5rem;
        color: var(--color-text-tertiary);
        margin-bottom: var(--space-2);
        display: block;
      }

      .upload-dropzone:hover i,
      .upload-dropzone.drag-over i {
        color: var(--color-primary);
      }

      .upload-dropzone .upload-text {
        font-size: var(--text-sm);
        color: var(--color-text-secondary);
      }

      .upload-dropzone .upload-text strong {
        color: var(--color-primary);
      }

      .upload-progress-bar {
        padding: var(--space-2) var(--space-5);
        font-size: var(--text-xs);
        color: var(--color-primary);
        font-weight: var(--font-medium);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }

      .upload-progress-bar .spinner {
        width: 14px;
        height: 14px;
        border: 2px solid var(--color-primary-subtle);
        border-top-color: var(--color-primary);
        border-radius: 50%;
        animation: spin 0.6s linear infinite;
      }

      @keyframes spin {
        to { transform: rotate(360deg); }
      }

      /* ─── File Grid ─── */
      .file-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
        gap: var(--space-3);
        padding: var(--space-4) var(--space-5);
      }

      .file-card {
        position: relative;
        border: 2px solid var(--color-border-light, #E8E5E0);
        border-radius: var(--radius-lg, 12px);
        overflow: hidden;
        cursor: pointer;
        transition: all 0.15s ease;
        background: var(--color-bg-primary);
      }

      .file-card:hover {
        border-color: var(--color-primary-subtle, #D4EEDF);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
      }

      .file-card.selected {
        border-color: var(--color-primary);
        background: var(--color-primary-light);
      }

      .select-indicator {
        position: absolute;
        top: 8px;
        left: 8px;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        border: 2px solid var(--color-border-light, #E8E5E0);
        background: white;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1;
        transition: all 0.15s ease;
      }

      .file-card.selected .select-indicator {
        background: var(--color-primary);
        border-color: var(--color-primary);
      }

      .select-indicator i {
        font-size: 10px;
        color: white;
        opacity: 0;
      }

      .file-card.selected .select-indicator i {
        opacity: 1;
      }

      .card-preview {
        height: 100px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--color-bg-tertiary);
        position: relative;
      }

      .card-preview img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .card-preview .preview-icon {
        font-size: 2rem;
        color: var(--color-text-tertiary);
        opacity: 0.5;
      }

      .card-preview .play-btn {
        position: absolute;
        bottom: 6px;
        right: 6px;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: var(--color-primary);
        color: white;
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        transition: background 0.15s ease;
        z-index: 1;
      }

      .card-preview .play-btn:hover {
        background: var(--color-primary-hover);
      }

      .card-info {
        padding: var(--space-2) var(--space-3);
      }

      .card-name {
        font-size: var(--text-xs, 11px);
        font-weight: var(--font-medium);
        color: var(--color-text-primary);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .card-meta {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        margin-top: 3px;
      }

      .type-badge {
        display: inline-flex;
        align-items: center;
        gap: 3px;
        padding: 1px 6px;
        border-radius: 100px;
        font-size: 9px;
        font-weight: var(--font-semibold);
        text-transform: uppercase;
        letter-spacing: 0.3px;
      }

      .card-size {
        font-size: 10px;
        color: var(--color-text-tertiary);
      }

      .card-delete {
        position: absolute;
        top: 8px;
        right: 8px;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.5);
        color: white;
        border: none;
        cursor: pointer;
        display: none;
        align-items: center;
        justify-content: center;
        font-size: 10px;
        z-index: 1;
        transition: background 0.15s ease;
      }

      .file-card:hover .card-delete {
        display: flex;
      }

      .card-delete:hover {
        background: var(--color-error);
      }

      /* ─── Empty State ─── */
      .empty-state {
        text-align: center;
        padding: var(--space-8) var(--space-5);
        color: var(--color-text-tertiary);
      }

      .empty-state i {
        font-size: 2.5rem;
        margin-bottom: var(--space-3);
        opacity: 0.4;
      }

      .empty-state .empty-title {
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--color-text-secondary);
        margin-bottom: var(--space-1);
      }

      .empty-state .empty-desc {
        font-size: var(--text-xs);
      }

      /* ─── Loading ─── */
      .loading-state {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: var(--space-8);
        color: var(--color-text-tertiary);
        gap: var(--space-2);
        font-size: var(--text-sm);
      }

      .loading-state .spinner {
        width: 18px;
        height: 18px;
        border: 2px solid var(--color-primary-subtle);
        border-top-color: var(--color-primary);
        border-radius: 50%;
        animation: spin 0.6s linear infinite;
      }

      /* ─── Footer ─── */
      .footer-content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
      }

      .selected-count {
        font-size: var(--text-sm);
        color: var(--color-text-secondary);
      }

      .footer-actions {
        display: flex;
        gap: var(--space-3);
      }
    `,
  ];

  constructor() {
    super();
    this._open = false;
    this._fileType = null;
    this._tags = '';
    this._uploadedBy = '';
    this._multiple = true;
    this._selectedIds = new Set();
    this._accept = '';
    this._files = [];
    this._loading = false;
    this._uploading = false;
    this._uploadProgress = '';
    this._searchQuery = '';
    this._playingId = null;
    this._dragOver = false;
    this._audioEl = null;
  }

  connectedCallback() {
    super.connectedCallback();
    mediaLibraryService.onStateChange(state => {
      if (state.open) {
        this._open = true;
        this._fileType = state.fileType || null;
        this._tags = state.tags || '';
        this._uploadedBy = state.uploadedBy || '';
        this._multiple = state.multiple !== false;
        this._selectedIds = new Set(state.selectedIds || []);
        this._accept = state.accept || '';
        this._searchQuery = '';
        this._playingId = null;
        this._loadFiles();
      } else {
        this._stopPlay();
        this._open = false;
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._stopPlay();
  }

  // ─── Data ───

  async _loadFiles() {
    this._loading = true;
    try {
      const data = await mediaService.loadFiles();
      if (data.success) {
        this._files = data.files || [];
      }
    } catch (err) {
      console.error('[MediaLibrary] Load failed:', err);
    } finally {
      this._loading = false;
    }
  }

  _getFilteredFiles() {
    let files = this._files;

    // Filter by file type pill
    if (this._fileType) {
      files = files.filter(f => f.fileType === this._fileType);
    }

    // Filter by search query
    if (this._searchQuery) {
      const q = this._searchQuery.toLowerCase();
      files = files.filter(f =>
        f.originalName.toLowerCase().includes(q) ||
        (f.displayName && f.displayName.toLowerCase().includes(q))
      );
    }

    return files;
  }

  // ─── Selection ───

  _toggleSelection(fileId) {
    const newSet = new Set(this._selectedIds);
    if (this._multiple) {
      if (newSet.has(fileId)) {
        newSet.delete(fileId);
      } else {
        newSet.add(fileId);
      }
    } else {
      // Single-select: replace
      if (newSet.has(fileId)) {
        newSet.clear();
      } else {
        newSet.clear();
        newSet.add(fileId);
      }
    }
    this._selectedIds = newSet;
  }

  _handleSelect() {
    const selected = this._files.filter(f => this._selectedIds.has(f.id));
    mediaLibraryService.select(selected);
  }

  _handleCancel() {
    mediaLibraryService.cancel();
  }

  // ─── Upload ───

  _triggerUpload() {
    const input = this.renderRoot.querySelector('#media-file-input');
    if (input) input.click();
  }

  async _handleFileSelect(e) {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;
    await this._uploadFiles(fileList);
    e.target.value = '';
  }

  async _uploadFiles(fileList) {
    this._uploading = true;
    const total = fileList.length;

    for (let i = 0; i < total; i++) {
      this._uploadProgress = total > 1 ? `Uploading ${i + 1} of ${total}...` : 'Uploading...';
      try {
        const data = await mediaService.uploadFile(fileList[i], {
          tags: this._tags,
          uploadedBy: this._uploadedBy,
        });
        if (data.success && data.media) {
          this._files = [data.media, ...this._files];
          // Auto-select newly uploaded file
          const newSet = new Set(this._selectedIds);
          newSet.add(data.media.id);
          this._selectedIds = newSet;
        }
      } catch (err) {
        console.error('[MediaLibrary] Upload failed:', err);
      }
    }

    this._uploading = false;
    this._uploadProgress = '';
  }

  _onDragOver(e) {
    e.preventDefault();
    this._dragOver = true;
  }

  _onDragLeave() {
    this._dragOver = false;
  }

  async _onDrop(e) {
    e.preventDefault();
    this._dragOver = false;
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      await this._uploadFiles(files);
    }
  }

  // ─── Audio Playback ───

  _togglePlay(file) {
    if (this._playingId === file.id) {
      this._stopPlay();
      return;
    }
    this._stopPlay();
    this._audioEl = new Audio(file.url);
    this._audioEl.onended = () => { this._playingId = null; };
    this._audioEl.play().catch(() => {});
    this._playingId = file.id;
  }

  _stopPlay() {
    if (this._audioEl) {
      this._audioEl.pause();
      this._audioEl.src = '';
      this._audioEl = null;
    }
    this._playingId = null;
  }

  // ─── Delete ───

  async _deleteFile(file, e) {
    e.stopPropagation();
    const confirmed = await confirmService.show({
      title: 'Delete File',
      message: `Delete "${file.displayName || file.originalName}"?`,
      description: 'This file will be permanently removed.',
      icon: 'fa-trash',
    });
    if (!confirmed) return;

    try {
      const data = await mediaService.deleteFile(file.id);
      if (data.success) {
        this._files = this._files.filter(f => f.id !== file.id);
        const newSet = new Set(this._selectedIds);
        newSet.delete(file.id);
        this._selectedIds = newSet;
      }
    } catch (err) {
      console.error('[MediaLibrary] Delete failed:', err);
    }
  }

  // ─── Helpers ───

  _formatSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  _getFileIcon(file) {
    return FILE_TYPE_ICONS[file.fileType] || 'fa-file';
  }

  _isPreviewable(file) {
    return file.fileType === 'image';
  }

  _isAudio(file) {
    return file.fileType === 'audio';
  }

  // ─── Render ───

  render() {
    return html`
      ${iconStyles}
      <voice-modal
        ?open=${this._open}
        title="Media Library"
        width="780px"
        height="75vh"
        @modal-close=${this._handleCancel}
      >
        <!-- Sticky toolbar -->
        <div slot="sticky">
          <div class="library-toolbar">
            <div class="search-bar">
              <i class="fa-sharp fa-light fa-magnifying-glass"></i>
              <input
                type="text"
                placeholder="Search files..."
                .value=${this._searchQuery}
                @input=${(e) => { this._searchQuery = e.target.value; }}
              />
            </div>
            <div class="filter-pills">
              ${FILTER_PILLS.map(pill => html`
                <button
                  class="pill ${this._fileType === pill.key ? 'active' : ''}"
                  @click=${() => { this._fileType = pill.key; }}
                >
                  ${pill.icon ? html`<i class="fa-sharp fa-light ${pill.icon}"></i>` : ''}
                  ${pill.label}
                </button>
              `)}
            </div>
          </div>
        </div>

        <!-- Body -->
        ${this._renderBody()}

        <!-- Footer -->
        <div slot="footer">
          <div class="footer-content">
            <span class="selected-count">
              ${this._selectedIds.size > 0
                ? `${this._selectedIds.size} selected`
                : 'No files selected'}
            </span>
            <div class="footer-actions">
              <button class="btn btn-secondary" @click=${this._handleCancel}>Cancel</button>
              <button
                class="btn btn-primary"
                ?disabled=${this._selectedIds.size === 0}
                @click=${this._handleSelect}
              >Select${this._selectedIds.size > 0 ? ` (${this._selectedIds.size})` : ''}</button>
            </div>
          </div>
        </div>
      </voice-modal>

      <input
        id="media-file-input"
        type="file"
        multiple
        accept=${this._accept || '*/*'}
        style="display:none"
        @change=${this._handleFileSelect}
      />
    `;
  }

  _renderBody() {
    if (this._loading) {
      return html`
        <div class="loading-state">
          <div class="spinner"></div>
          Loading files...
        </div>
      `;
    }

    const filtered = this._getFilteredFiles();

    return html`
      <!-- Upload drop zone -->
      <div
        class="upload-dropzone ${this._dragOver ? 'drag-over' : ''}"
        @click=${this._triggerUpload}
        @dragover=${this._onDragOver}
        @dragleave=${this._onDragLeave}
        @drop=${this._onDrop}
      >
        <i class="fa-sharp fa-light fa-cloud-arrow-up"></i>
        <div class="upload-text">
          <strong>Click to upload</strong> or drag and drop
        </div>
      </div>

      ${this._uploading ? html`
        <div class="upload-progress-bar">
          <div class="spinner"></div>
          ${this._uploadProgress}
        </div>
      ` : ''}

      ${filtered.length === 0 ? html`
        <div class="empty-state">
          <i class="fa-sharp fa-light fa-folder-open"></i>
          <div class="empty-title">No files found</div>
          <div class="empty-desc">
            ${this._files.length === 0
              ? 'Upload your first file to get started'
              : 'Try a different search or filter'}
          </div>
        </div>
      ` : html`
        <div class="file-grid">
          ${filtered.map(file => this._renderFileCard(file))}
        </div>
      `}
    `;
  }

  _renderFileCard(file) {
    const selected = this._selectedIds.has(file.id);
    const colors = FILE_TYPE_COLORS[file.fileType] || FILE_TYPE_COLORS.document;

    return html`
      <div
        class="file-card ${selected ? 'selected' : ''}"
        @click=${() => this._toggleSelection(file.id)}
      >
        <div class="select-indicator">
          <i class="fa-sharp fa-solid fa-check"></i>
        </div>

        <button class="card-delete" @click=${(e) => this._deleteFile(file, e)} title="Delete">
          <i class="fa-sharp fa-light fa-trash"></i>
        </button>

        <div class="card-preview">
          ${this._isPreviewable(file) ? html`
            <img src="${file.url}" alt="${file.originalName}" loading="lazy" />
          ` : html`
            <i class="fa-sharp fa-light ${this._getFileIcon(file)} preview-icon"></i>
          `}
          ${this._isAudio(file) ? html`
            <button
              class="play-btn"
              @click=${(e) => { e.stopPropagation(); this._togglePlay(file); }}
              title="${this._playingId === file.id ? 'Stop' : 'Play'}"
            >
              <i class="fa-sharp fa-solid ${this._playingId === file.id ? 'fa-stop' : 'fa-play'}"></i>
            </button>
          ` : ''}
        </div>

        <div class="card-info">
          <div class="card-name" title="${file.displayName || file.originalName}">${file.displayName || file.originalName}</div>
          <div class="card-meta">
            <span
              class="type-badge"
              style="background:${colors.bg};color:${colors.text};"
            >
              ${file.fileType}
            </span>
            <span class="card-size">${this._formatSize(file.fileSize)}</span>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('voice-media-library', VoiceMediaLibrary);
